/**
 * Thrown by value-object factories when inputs would produce an illegal state.
 * Value objects "make illegal states unrepresentable" (ADR-0004): construction
 * is the single validation gate, so callers can trust an instance once built.
 */
export class ValueObjectError extends Error {
  override readonly name = 'ValueObjectError'
}

/**
 * UID-603 (ADR-0176, review W4 fifth pass) — the RFC 7807 Problem `type` both
 * `apps/api`'s cursor-rejecting routes (`GET /rfq`, `GET /parts`) and
 * `apps/web`'s `CursorRejectedError` detection (`usePartsLibrary.ts`,
 * `rfq-list-service.ts`) agree on, so the frontend can tell "this 400 is a
 * cursor rejection, retry via reload()/refetch()" apart from "this 400 is a
 * DIFFERENT client error that happens to carry a cursor param" (an unrelated
 * validation failure, a `workspaceId`+`limit` refusal, …) WITHOUT guessing
 * from the response's `title`/`detail` text.
 *
 * A message-substring check was considered and rejected: several genuine
 * cursor-rejection messages on the API side don't contain the word "cursor"
 * at all ("malformed anchor id", "empty token", "token too large", "not a
 * decodable token"), so matching on text would have SILENTLY BROKEN those
 * real cases rather than just filtering false positives. A stable,
 * machine-readable discriminator is the only correct fix.
 */
export const CURSOR_REJECTED_PROBLEM_TYPE = 'https://misumi.dev/problems/cursor-rejected'

export function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new ValueObjectError(message)
  }
}
